import { defaultSchema } from 'rehype-sanitize'
import type { Schema } from 'hast-util-sanitize'

/**
 * Sanitize schema for chat/doc markdown rendered via `rehype-raw` (see Markdown.tsx).
 * `rehype-raw` re-parses the whole tree through an HTML parser so LLM/tool output
 * containing literal `<script>`, `onerror=`, `<iframe>`, etc. renders live in the DOM
 * unless something strips it — this schema is that step. Built on
 * `hast-util-sanitize`'s default (GitHub-style) schema, which already covers
 * headings/tables/lists/task-list checkboxes/code `className` etc.
 *
 * Two deliberate deviations from the default schema, both required by features that
 * already exist in this file and are covered by tests:
 *
 * 1. `div` gains `className`/`dataSectionId`/`dataHeadingLevel`. `remarkCollapsibleSections`
 *    wraps headings in `<div data-section-id data-heading-level>` for the collapsible-
 *    sections feature. Note: by the time this schema runs (after `rehype-raw`, which
 *    stringifies + re-parses the whole tree through parse5), hyphenated hProperties keys
 *    have already been camelCased — the schema must use `dataSectionId`, not
 *    `'data-section-id'`, or the attributes are silently dropped despite "matching".
 *
 * 2. `protocols.href` is removed entirely (left unrestricted for the elements this
 *    schema allows — in practice only `<a>`, since no other allowed tag carries `href`).
 *    `url-transform.ts`'s `markdownUrlTransform` deliberately preserves the raw href
 *    (including `file:`/`javascript:`) so the custom `a` component (Markdown.tsx) can
 *    route file:// links through `onFileClick` via `resolveMarkdownLinkTarget`, while
 *    still rendering a separately re-sanitized `safeHref` as the actual DOM attribute
 *    (`defaultUrlTransform` blanks dangerous schemes there). If this sanitize schema
 *    also restricted `href` protocols, the attribute would be stripped from the hast
 *    tree before react-markdown ever calls `urlTransform` or the `a` override, silently
 *    breaking file-link click routing (see `__tests__/markdown-link-routing.test.ts`).
 *    `img[src]` keeps the default http/https-only restriction — nothing needs a raw
 *    non-http image src, and `markdownUrlTransform` already blanks bad img src too.
 */
export const markdownSanitizeSchema: Schema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    div: [...(defaultSchema.attributes?.div ?? []), 'className', 'dataSectionId', 'dataHeadingLevel'],
  },
  protocols: {
    ...defaultSchema.protocols,
    href: undefined,
  },
}
